import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatJPY, todayISO } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Loader2, Package } from "lucide-react";
import { toast } from "sonner";
import { DOC_CONFIG } from "@/pages/billing/config";

const emptyLine = () => ({
  description: "", sku: "", quantity: 1, unit: "個", unit_price: 0,
  tax_rate: 10, tax_category: "課税", discount: 0,
  office: "", service_category: "", department: "", employment_support: false,
});

export default function DocFormDialog({ docType, open, onOpenChange, initial, onSaved }) {
  const cfg = DOC_CONFIG[docType];
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showWelfare, setShowWelfare] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
    api.get("/billing/products").then((r) => setProducts(r.data || [])).catch(() => {});
    if (initial) {
      setForm({
        customer_id: initial.customer_id || "",
        issue_date: initial.issue_date || todayISO(),
        due_date: initial.due_date || "",
        expiry_date: initial.expiry_date || "",
        delivery_date: initial.delivery_date || "",
        notes: initial.notes || "",
        revenue_type: initial.revenue_type || "sales",
        withholding: !!initial.withholding,
        lines: (initial.lines && initial.lines.length ? initial.lines : [emptyLine()]).map((l) => ({ ...emptyLine(), ...l })),
      });
      setShowWelfare(initial.revenue_type === "employment_support");
    } else {
      setForm({
        customer_id: "", issue_date: todayISO(), due_date: "", expiry_date: "", delivery_date: "",
        notes: "", revenue_type: "sales", withholding: false, lines: [emptyLine()],
      });
      setShowWelfare(false);
    }
  }, [open, initial]);

  if (!form) return null;

  const setLine = (i, patch) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], ...patch };
    setForm({ ...form, lines });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, emptyLine()] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const applyProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setLine(i, { description: p.name, sku: p.sku, unit: p.unit, unit_price: p.unit_price, tax_rate: p.tax_rate, tax_category: p.tax_category });
  };

  const totals = () => {
    let subtotal = 0; const byRate = {};
    form.lines.forEach((l) => {
      const base = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0) - (Number(l.discount) || 0);
      subtotal += base;
      const k = String(Number(l.tax_rate) || 0);
      byRate[k] = (byRate[k] || 0) + base;
    });
    let tax = 0;
    Object.entries(byRate).forEach(([k, base]) => { tax += Math.round(base * (Number(k) / 100)); });
    const wh = form.withholding ? Math.round(subtotal * 0.1021) : 0;
    return { subtotal: Math.round(subtotal), tax: Math.round(tax), wh, total: Math.round(subtotal) + Math.round(tax) - wh };
  };
  const t = totals();

  const save = async () => {
    if (!form.customer_id) { toast.error("取引先を選択してください"); return; }
    if (!form.lines.length || !form.lines.some((l) => l.description)) { toast.error("明細を入力してください"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (initial?.id) {
        await api.put(`/billing/documents/${docType}/${initial.id}`, payload);
        toast.success("更新しました");
      } else {
        await api.post(`/billing/documents/${docType}`, payload);
        toast.success(`${cfg.title}を作成しました`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="doc-form-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? `${cfg.title}を編集` : `${cfg.title}を作成`}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>取引先</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
              <SelectTrigger data-testid="doc-customer-select"><SelectValue placeholder="取引先を選択" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {cfg.dateFields.map((d) => (
            <div key={d.key}>
              <Label>{d.label}</Label>
              <Input type="date" value={form[d.key] || ""} onChange={(e) => setForm({ ...form, [d.key]: e.target.value })} data-testid={`doc-${d.key}`} />
            </div>
          ))}
        </div>

        {/* Line items */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <Label>明細</Label>
            <button onClick={addLine} className="text-sm text-indigo-600 flex items-center gap-1 hover:text-indigo-800" data-testid="doc-add-line">
              <Plus className="h-4 w-4" /> 行を追加
            </button>
          </div>
          {form.lines.map((l, i) => (
            <div key={i} className="rounded-lg border border-stone-200 p-3 bg-stone-50/60">
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-12 md:col-span-4">
                  <Label className="text-xs">品目・内容</Label>
                  <Input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} data-testid={`doc-line-desc-${i}`} />
                  {products.length > 0 && (
                    <Select onValueChange={(v) => applyProduct(i, v)}>
                      <SelectTrigger className="mt-1 h-7 text-xs text-stone-500"><Package className="h-3 w-3 mr-1" /><span>商品から選択</span></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}（{formatJPY(p.unit_price)}）</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <div className="col-span-4 md:col-span-1">
                  <Label className="text-xs">数量</Label>
                  <Input type="number" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} data-testid={`doc-line-qty-${i}`} />
                </div>
                <div className="col-span-4 md:col-span-1">
                  <Label className="text-xs">単位</Label>
                  <Input value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Label className="text-xs">単価</Label>
                  <Input type="number" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} data-testid={`doc-line-price-${i}`} />
                </div>
                <div className="col-span-4 md:col-span-1">
                  <Label className="text-xs">値引</Label>
                  <Input type="number" value={l.discount} onChange={(e) => setLine(i, { discount: e.target.value })} />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Label className="text-xs">税率</Label>
                  <Select value={String(l.tax_rate)} onValueChange={(v) => setLine(i, { tax_rate: Number(v) })}>
                    <SelectTrigger data-testid={`doc-line-rate-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="8">8%（軽減）</SelectItem>
                      <SelectItem value="0">0%（非課税）</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3 md:col-span-1 text-right">
                  <Label className="text-xs">金額</Label>
                  <div className="text-sm font-medium py-2">{formatJPY((Number(l.quantity) || 0) * (Number(l.unit_price) || 0) - (Number(l.discount) || 0))}</div>
                </div>
                <div className="col-span-1 flex justify-end">
                  <button onClick={() => removeLine(i)} className="text-stone-400 hover:text-red-500 p-2" data-testid={`doc-line-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {showWelfare && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 pt-2 border-t border-stone-200">
                  <Input placeholder="事業所" value={l.office} onChange={(e) => setLine(i, { office: e.target.value })} className="text-xs h-8" />
                  <Input placeholder="サービス区分" value={l.service_category} onChange={(e) => setLine(i, { service_category: e.target.value })} className="text-xs h-8" />
                  <Input placeholder="部門" value={l.department} onChange={(e) => setLine(i, { department: e.target.value })} className="text-xs h-8" />
                  <label className="flex items-center gap-2 text-xs text-stone-600">
                    <Checkbox checked={l.employment_support} onCheckedChange={(v) => setLine(i, { employment_support: !!v })} /> 就労支援事業
                  </label>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Options + totals */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="space-y-3">
            <div>
              <Label>収益区分</Label>
              <Select value={form.revenue_type} onValueChange={(v) => { setForm({ ...form, revenue_type: v }); setShowWelfare(v === "employment_support"); }}>
                <SelectTrigger data-testid="doc-revenue-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">一般売上</SelectItem>
                  <SelectItem value="employment_support">就労支援事業収入</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-stone-400 mt-1">国保連の障害福祉サービス収入とは区別されます。</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-700">
              <Checkbox checked={form.withholding} onCheckedChange={(v) => setForm({ ...form, withholding: !!v })} data-testid="doc-withholding" /> 源泉徴収（10.21%）
            </label>
            <label className="flex items-center gap-2 text-sm text-stone-700">
              <Checkbox checked={showWelfare} onCheckedChange={(v) => setShowWelfare(!!v)} /> 事業所・サービス区分を明細に表示
            </label>
            <div>
              <Label>備考</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="doc-notes" />
            </div>
          </div>
          <div className="rounded-lg border border-stone-200 p-4 bg-white h-fit">
            <div className="flex justify-between text-sm py-1"><span className="text-stone-500">小計</span><span>{formatJPY(t.subtotal)}</span></div>
            <div className="flex justify-between text-sm py-1"><span className="text-stone-500">消費税</span><span>{formatJPY(t.tax)}</span></div>
            {t.wh > 0 && <div className="flex justify-between text-sm py-1 text-rose-600"><span>源泉徴収税</span><span>-{formatJPY(t.wh)}</span></div>}
            <div className="flex justify-between text-base font-bold py-2 border-t border-stone-200 mt-1 text-indigo-700"><span>合計（税込）</span><span data-testid="doc-total">{formatJPY(t.total)}</span></div>
          </div>
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-stone-600 hover:text-stone-900">キャンセル</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2" data-testid="doc-save-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} 保存
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
